"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ClipboardCheck, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const checklistItems = ["Projektplan & samordning", "Skiss & designplanering", "Samordning av material", "Auktoriserat VVS (Säker Vatten)", "Färdigställande & genomgång"];
export default function ExpertiseAndCoordination() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="expertise-and-coordination" className="py-16 md:py-24 lg:py-32 bg-secondary text-secondary-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={sectionRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="flex flex-col items-start max-w-2xl">
            <Badge variant="outline" className="mb-6 bg-background/50 backdrop-blur-sm border-border text-foreground">Totalentreprenad i Karlstad</Badge>
            <h2 className="font-bold font-default leading-tight mb-6 text-foreground !text-4xl">Komplett badrumsentreprenad – från första skiss till godkänt våtrum</h2>
            <p className="text-muted-foreground font-sans leading-relaxed !text-lg">När du renoverar badrum i Karlstad kan Proffsbadrum samordna hela projektet från planering till färdigt badrum. Vi hjälper till med material, tidsplanering och samordning av de olika yrkesgrupperna, så att du får en tydlig kontakt genom hela processen. VVS-installationerna utförs av vårt moderbolag <Link to="https://rs-ab.com/" newTab className="text-blue-600 underline hover:text-blue-800">RSAB Rörservice AB</Link>, som är ett auktoriserat VVS-företag enligt Säker Vattens branschregler.</p>
          </div>

          {}
          <div className="relative mx-auto w-full max-w-md lg:max-w-lg">
            {}
            <div className="absolute -inset-4 bg-primary/5 rounded-2xl transform rotate-3 scale-105 transition-transform duration-700 hover:rotate-6"></div>
            <div className="absolute -inset-4 bg-background/40 rounded-2xl transform -rotate-2 scale-105 backdrop-blur-sm border border-border/50"></div>
            
            {}
            <div className="relative bg-background rounded-xl shadow-xl border border-border p-6 md:p-8 z-10">
              {}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border">
                <div className="bg-primary/10 p-3 rounded-lg text-primary shrink-0">
                  <ClipboardCheck className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="font-default font-bold text-xl text-foreground">Vår projektplan för helhetsansvar</h3>
                  <p className="text-sm text-muted-foreground font-sans">Helhetsansvar Karlstad</p>
                </div>
              </div>

              {}
              <div className="space-y-5">
                {checklistItems.map((item, i) => <div data-index={i} key={i} className="flex items-start gap-4 group">
                    <div className="mt-0.5 bg-secondary p-1.5 rounded-md text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-foreground font-medium text-base md:text-lg font-sans pt-0.5">
                      {item}
                    </span>
                  </div>)}
              </div>

              {}
              <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span>Säker Vatten-auktoriserad</span>
                </div>
                <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center">
                  <span className="text-xs font-bold text-foreground">RSAB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}